import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Globe, Code, Smartphone, Shield, Monitor, Search, LayoutDashboard, Link } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const portfolioIcons = [
  { icon: Code, color: "text-primary", bg: "bg-primary/15" },
  { icon: Globe, color: "text-accent", bg: "bg-accent/15" },
  { icon: Smartphone, color: "text-sky-400", bg: "bg-sky-500/15" },
  { icon: Shield, color: "text-green-400", bg: "bg-green-500/15" },
  { icon: Link, color: "text-purple-400", bg: "bg-purple-500/15" },
  { icon: Monitor, color: "text-orange-400", bg: "bg-orange-500/15" },
  { icon: LayoutDashboard, color: "text-cyan-400", bg: "bg-cyan-500/15" },
  { icon: Search, color: "text-rose-400", bg: "bg-rose-500/15" },
];

export const PortfolioSection = ({ portfolio }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  if (!portfolio || portfolio.length === 0) return null;

  return (
    <section
      id="portfolio"
      className="py-16 md:py-24 scroll-mt-24 relative"
      data-testid="portfolio-section"
      aria-label="Web Development Portfolio"
      ref={ref}
    >
      <div className="mx-auto max-w-[var(--page-max)] px-4 md:px-6">
        <motion.div
          variants={container}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div variants={reveal} className="text-center mb-12">
            <span className="mono-label text-primary mb-3 block">Web Development</span>
            <h2 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
              Creations & Portfolio
            </h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto">
              Websites and applications built by Tylegn Gaming & Entertainment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {portfolio.map((item, i) => {
              const style = portfolioIcons[i % portfolioIcons.length];
              return (
                <motion.div key={item.name} variants={reveal}>
                  <Card
                    className="p-6 bg-card border-border h-full flex flex-col glow-card"
                    data-testid={`portfolio-card-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-10 h-10 rounded-xl ${style.bg} flex items-center justify-center`}>
                        <style.icon className={`w-5 h-5 ${style.color}`} />
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-secondary text-muted-foreground border border-border text-[10px]"
                      >
                        {item.type}
                      </Badge>
                    </div>

                    <h3 className="font-semibold text-base mb-2" style={{ fontFamily: 'var(--font-display)' }}>
                      {item.name}
                    </h3>

                    <p className="text-sm text-muted-foreground leading-[1.55] flex-grow mb-4">
                      {item.description}
                    </p>

                    <Button
                      asChild
                      variant="secondary"
                      size="sm"
                      className="bg-secondary text-secondary-foreground border border-border hover:border-primary/50 w-full"
                    >
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Visit ${item.name}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5 mr-2" />
                        Visit Site
                      </a>
                    </Button>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
